import { useState } from 'react';
import debounce from 'lodash/debounce';
import TextInput from '../inputs/form-inputs/TextInput';
import { QueryClientProvider, useQuery } from '@tanstack/react-query';
import { queryClient } from '../../application';

function CategorySearch({ endpoints }) {
  const { categoryUrl, categoryValueUrl, searchUrl } = endpoints;
  const [keyword, setKeyword] = useState('');

  // Debounce input changes
  const handleOnChange = debounce((e) => {
    setKeyword(e.target.value);
  }, 500);

  const {
    data = [],
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['categorySearch', keyword],
    queryFn: async () => {
      if (!keyword) return [];
      const response = await fetch(`${searchUrl}.json`, {
        method: 'POST',
        credentials: 'same-origin',
        body: JSON.stringify({ keyword }),
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      });
      if (!response.ok) throw new Error('Failed to search categories or category values');
      const data = await response.json();
      return data.results;
    },
    enabled: !!keyword,
    keepPreviousData: true,
  });

  return (
    <div className="d-flex flex-column gap-3">
      <div>
        <TextInput placeholder="Search for a category..." onChange={handleOnChange} />
      </div>
      <div className="card table-responsive">
        <table className="table table-sm table-hover table-nowrap card-table">
          <thead>
            <tr>
              <th scope="col" style={{ width: 40 }}>
                #
              </th>
              <th scope="col" style={{ width: 300 }}>
                ID
              </th>
              <th scope="col" style={{ width: 600 }}>
                Name
              </th>
              <th scope="col" style={{ width: 100 }}>
                Type
              </th>
              <th scope="col" style={{ width: 60 }}>
                Go to
              </th>
            </tr>
          </thead>
          <tbody>
            {!isLoading && !isError && data.length === 0 && !keyword && (
              <tr>
                <td colSpan={5} className="text-center">
                  Enter your search term to find a category or category value.
                </td>
              </tr>
            )}
            {isLoading && (
              <tr>
                <td colSpan={5} className="text-center">
                  <div className="spinner-border" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </td>
              </tr>
            )}
            {!isLoading && isError && (
              <tr>
                <td colSpan={5} className="text-center text-danger">
                  Error searching for category and category value. Please try again.
                </td>
              </tr>
            )}
            {!isLoading && data && data.length === 0 && keyword && (
              <tr>
                <td colSpan={5} className="text-center">
                  No category and category value found for the search query.
                </td>
              </tr>
            )}
            {data &&
              data.length > 0 &&
              data.map((result, index) => (
                <tr key={result.id}>
                  <th scope="row">{index + 1}</th>
                  <td>{result.id}</td>
                  <td>{result.name ?? result.identifier}</td>
                  <td className="text-muted">{result.name ? 'Category' : 'Category Value'}</td>
                  <td>
                    <a
                      href={
                        result.name
                          ? categoryUrl.replace(':category_id', result.id).replace(':category_name', result.name)
                          : categoryValueUrl
                              .replace(':category_id', result.parent_category_id)
                              .replace(':category_name', result.parent_category_name)
                              .replace(':id', result.id)
                      }
                      target="_blank"
                      className="btn btn-sm btn-primary d-flex align-items-center gap-2"
                      style={{ width: 60 }}
                    >
                      Visit
                      <i className="fe fe-external-link" />
                    </a>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function CategorySearchApp(props) {
  return (
    <QueryClientProvider client={queryClient}>
      <CategorySearch {...props} />
    </QueryClientProvider>
  );
}
